import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell as BellIcon, ChevronRight } from 'lucide-react';
import { myDayApi, KIND_COLOUR, type BellItem } from '../lib/myday';
import { useAuth } from '../stores/auth';
import { formatDate } from '../lib/viz';
import { SkeletonRows } from './ui';

/**
 * The header bell: how many things are waiting (open My Day tasks + unread
 * notifications) and the top eight. Polls every minute; opening it marks the
 * notifications read.
 */
export function HeaderBell() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasDay = can('myDay.read');

  const q = useQuery({ queryKey: ['/my-day/bell'], queryFn: myDayApi.bell, refetchInterval: 60_000, refetchIntervalInBackground: false });
  const bell = q.data;
  const n = bell?.count ?? 0;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const r = await q.refetch();
      // What you have now seen is no longer news. The list stays on screen until it closes.
      if ((r.data?.unread ?? 0) > 0) {
        try { await myDayApi.markNotificationsRead(); } catch { /* the next poll will retry */ }
      }
    } else void qc.invalidateQueries({ queryKey: ['/my-day/bell'] });
  };

  const go = (item: BellItem) => {
    setOpen(false);
    void qc.invalidateQueries({ queryKey: ['/my-day/bell'] });
    navigate(item.url ?? (hasDay ? '/app/my-day' : '/app'));
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} title={n ? `${n} waiting` : 'Nothing waiting'} aria-label="Notifications"
        className={`relative grid h-8 w-8 place-items-center rounded-lg transition hover:bg-ink-100 hover:text-ink-900 ${n ? 'text-ink-700' : 'text-ink-500'}`}>
        <BellIcon className="h-4 w-4" />
        {n > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-on-primary ring-2 ring-white">
            {n > 99 ? '99+' : n}
          </span>
        )}
      </button>

      {open && (
        <div className="menu-pop fixed inset-x-3 top-[56px] z-50 overflow-hidden rounded-lg border border-ink-200 bg-white shadow-float sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[380px]">
          <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2">
            <span className="text-[13px] font-semibold text-ink-900">Waiting on you</span>
            <span className="text-[11.5px] tabular-nums text-ink-400">
              {bell ? `${bell.tasks} task${bell.tasks === 1 ? '' : 's'}${bell.unread ? ` · ${bell.unread} new` : ''}` : '…'}
            </span>
          </div>
          {!bell?.items.length ? (
            <div className="px-4 py-10 text-center text-[12.5px] text-ink-500">{q.isLoading ? <SkeletonRows rows={3} cols={1} className="text-left" /> : 'Nothing waiting. Enjoy it.'}</div>
          ) : (
            <div className="max-h-[60vh] divide-y divide-ink-100 overflow-y-auto">
              {bell.items.map((i) => (
                <button key={`${i.type}:${i.id}`} onClick={() => go(i)}
                  className="block w-full px-3 py-2 text-left transition hover:bg-ink-50"
                  style={{ boxShadow: `inset 3px 0 0 ${i.type === 'notification' ? '#5b7fd1' : i.overdue ? '#d9534f' : KIND_COLOUR[i.id.split(':')[0] ?? ''] ?? '#8fa3b8'}` }}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12.5px] font-medium text-ink-900">
                        {i.type === 'notification' && <span className="mr-1.5 rounded-full bg-blue-50 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-blue-700">New</span>}
                        {i.title}
                      </div>
                      {i.detail && <div className="truncate text-[11.5px] text-ink-500">{i.detail}</div>}
                    </div>
                    {i.due && <span className={`shrink-0 text-[11px] ${i.overdue ? 'font-medium text-red-600' : 'text-ink-400'}`}>{formatDate(i.due).slice(0, 6)}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
          {hasDay && (
            <button onClick={() => { setOpen(false); navigate('/app/my-day'); }} className="flex w-full items-center justify-center gap-1 border-t border-ink-100 bg-ink-50/60 px-3 py-2 text-[12.5px] font-medium text-bronze-600 transition hover:bg-ink-100">
              Open My Day{bell && bell.tasks > 0 && bell.items.filter((x) => x.type === 'task').length < bell.tasks ? ` — ${bell.tasks - bell.items.filter((x) => x.type === 'task').length} more` : ''}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
